import React, {
  useRef,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { isEmpty, isArray } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Select, DateTime } from '~/components/Form';
import { t } from '~/helpers';
import { getEveryMonth, getYearsInterval } from '~/helpers/dateTime';
import api from '~/services/api';

import * as S from './styles';

export default function TypeFilters({
  options,
  initials,
  onCallback,
  validate,
  numberOfYears,
  initialURL,
  finalURL,
  code,
}) {
  const [typeSelect, setTypeSelect] = useState(null);
  const [shift, setShift] = useState({ initial: [], final: [] });

  const formRef = useRef(null);

  const optionsArray = useMemo(
    () => [
      { label: t('s.geral.filtro.periododatahora'), value: 1 },
      { label: t('s.geral.filtro.periododata'), value: 2 },
      { label: t('s.geral.filtro.periodomesanoiniciofim'), value: 3 },
      { label: t('s.geral.filtro.periodomesano'), value: 4 },
      { label: t('s.geral.filtro.turno'), value: 5 },
    ],
    []
  );

  const legend = useMemo(
    () =>
      typeSelect
        ? optionsArray.find((o) => o.value === typeSelect).label
        : null,
    [typeSelect, optionsArray]
  );

  const handleSendWithModification = useCallback(() => {
    formRef.current.submitForm();
  }, []);

  const handleGetShift = useCallback(
    async (value, position) => {
      try {
        const URL = position === 'initial' ? initialURL : finalURL;
        const query =
          position === 'initial'
            ? { datahorainicio: `${value} 00:00:00` }
            : { datahorafinal: `${value} 00:00:00` };

        const { data } = await api.get(URL, {
          params: { ...query, codigoposto: code },
        });

        setShift((state) => ({
          ...state,
          [position]: data.map((item) => ({
            ...item,
            label: `${item.turn_num_turn} - ${
              position === 'initial' ? item.hora_ini : item.hora_fim
            }`,
            value: item.turn_num_turn,
          })),
        }));
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [initialURL, finalURL, code]
  );

  const renderInputs = useCallback(
    (value) => {
      // Período de Data/Hora
      if (value === 1)
        return (
          <>
            <DateTime
              time
              name="datahora_inicio"
              label={t('s.cadastro.contrato.datainicialcontrato')}
              getValue={handleSendWithModification}
            />

            <DateTime
              time
              name="datahora_fim"
              label={t('s.cadastro.contrato.datafinalcontrato')}
              getValue={handleSendWithModification}
            />
          </>
        );

      // Período Data
      if (value === 2)
        return (
          <>
            <DateTime
              name="data_inicio"
              label={t('s.cadastro.contrato.datainicialcontrato')}
              getValue={handleSendWithModification}
            />

            <DateTime
              name="data_fim"
              label={t('s.cadastro.contrato.datafinalcontrato')}
              getValue={handleSendWithModification}
            />
          </>
        );

      // Período mês/ano inicio e fim
      if (value === 3) {
        const months = getEveryMonth().map((month, key) => ({
          value: key + 1,
          label: month,
        }));

        const years = getYearsInterval(numberOfYears).map((year) => ({
          value: year,
          label: year,
        }));

        return (
          <>
            <Select
              name="mes_inicio"
              label={t('s.geral.filtro.mesinicio')}
              options={months}
              getValue={handleSendWithModification}
            />

            <Select
              name="ano_inicio"
              label={t('s.geral.filtro.anoinicio')}
              options={years}
              getValue={handleSendWithModification}
            />

            <Select
              name="mes_fim"
              label={t('s.geral.filtro.mesfim')}
              options={months}
              getValue={handleSendWithModification}
            />

            <Select
              name="ano_fim"
              label={t('s.geral.filtro.anofim')}
              options={years}
              getValue={handleSendWithModification}
            />
          </>
        );
      }

      // Período Mês e Ano
      if (value === 4) {
        const months = getEveryMonth().map((month, key) => ({
          value: key + 1,
          label: month,
        }));

        const years = getYearsInterval(numberOfYears).map((year) => ({
          value: year,
          label: year,
        }));

        return (
          <>
            <Select
              name="mes"
              label={t('s.geral.filtro.mes')}
              options={months}
              getValue={handleSendWithModification}
            />

            <Select
              name="ano"
              label={t('s.geral.filtro.ano')}
              options={years}
              getValue={handleSendWithModification}
            />
          </>
        );
      }

      // Turno
      if (value === 5)
        return (
          <>
            <DateTime
              name="data_inicio"
              label={t('s.cadastro.contrato.datainicialcontrato')}
              getValue={(val) => handleGetShift(val, 'initial')}
            />

            <Select
              name="turno_inicio"
              label={t('s.geral.filtro.turnoinicio')}
              options={shift.initial}
              getValue={handleSendWithModification}
            />

            <DateTime
              name="data_fim"
              label={t('s.cadastro.contrato.datafinalcontrato')}
              getValue={(val) => handleGetShift(val, 'final')}
            />

            <Select
              name="turno_fim"
              label={t('s.geral.filtro.turnofim')}
              options={shift.final}
              getValue={handleSendWithModification}
            />
          </>
        );
    },
    [numberOfYears, shift]
  );

  const handleSubmit = useCallback(
    async (data) => {
      try {
        const isValid = validate.filter((val) => val.field === typeSelect);
        const schemaValidate = isEmpty(isValid) ? {} : isValid.shape;
        const schema = Yup.object().shape(schemaValidate);

        await schema.validate(data, {
          abortEarly: false,
        });

        const sendData = formRef.current.getData();

        if (sendData.turno_inicio) {
          sendData.turno_inicio = shift.initial.find(
            (s) => s.value === sendData.turno_inicio
          );
        }

        if (sendData.turno_fim) {
          sendData.turno_fim = shift.final.find(
            (s) => s.value === sendData.turno_fim
          );
        }

        onCallback(sendData);
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        }
      }
    },
    [validate, typeSelect, shift]
  );

  useEffect(() => {
    if (options.length === 1) setTypeSelect(options[0].value);
  }, [options]);

  return (
    <Form ref={formRef} initialData={initials} onSubmit={handleSubmit}>
      {options.length > 1 && (
        <Select
          name="tipo"
          label={t('s.geral.filtro.tipofiltro')}
          getValue={(value) => setTypeSelect(Number(value))}
          options={isEmpty(options) ? optionsArray : options}
        />
      )}

      {optionsArray.map((o) => o.value).includes(typeSelect) && (
        <S.GroupInput grid={[1, 3, 4, 5].includes(typeSelect)}>
          <legend>{legend}</legend>
          <div className="__container">{renderInputs(typeSelect)}</div>
        </S.GroupInput>
      )}
    </Form>
  );
}

TypeFilters.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  numberOfYears: PropTypes.number,
  initials: PropTypes.objectOf(PropTypes.any),
  onCallback: PropTypes.func.isRequired,
  validate: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.number.isRequired,
      shape: PropTypes.objectOf(PropTypes.any).isRequired,
    })
  ),
  initialURL: PropTypes.string,
  finalURL: PropTypes.string,
  code: PropTypes.string,
};

TypeFilters.defaultProps = {
  numberOfYears: 20,
  options: [],
  validate: [],
  initials: {},
  initialURL: '',
  finalURL: '',
  code: '',
};
